import { CSSProperties, useEffect, useState } from "react";
import { ordersAnalyticsApi } from "@/features/orders-analytics/api/orders-analytics.api";
import { analyticsTheme } from "@/features/orders-analytics/theme/tokens";
import {
    KpiMetric,
    OrdersAnalyticsFilters,
    OrdersCustomersResponse,
    OrdersFunnelResponse,
    OrdersMonetizationResponse,
    OrdersPerformanceResponse,
    OrdersQualityResponse,
    OrdersSegmentationResponse,
    OrdersSummaryResponse,
    OrdersTodayResponse,
    OrdersTrendsResponse,
    SegmentationDimension,
    SegmentationRow,
    TrendsPoint,
} from "@/features/orders-analytics/types/orders-analytics.types";
import { formatters } from "@/features/orders-analytics/utils/formatters";

const DIMENSION_OPTIONS: { value: SegmentationDimension; label: string }[] = [
    { value: "client", label: "Client" },
    { value: "operationType", label: "Operation type" },
    { value: "country", label: "Country" },
    { value: "withdrawProvider", label: "Withdraw provider" },
    { value: "onrampProvider", label: "Onramp provider" },
    { value: "rail", label: "Rail" },
    { value: "destinationRail", label: "Destination rail" },
    { value: "pair", label: "Pair" },
    { value: "depositType", label: "Deposit type" },
    { value: "amountRange", label: "Amount range" },
];

type Props = {
    filters: OrdersAnalyticsFilters;
    summary: OrdersSummaryResponse | null;
    trends: OrdersTrendsResponse | null;
    today: OrdersTodayResponse | null;
    funnel: OrdersFunnelResponse | null;
    performance: OrdersPerformanceResponse | null;
    monetization: OrdersMonetizationResponse | null;
    quality: OrdersQualityResponse | null;
    segmentation: OrdersSegmentationResponse | null;
    customers: OrdersCustomersResponse | null;
};

export function ExperimentalDashboard({
    filters,
    summary,
    trends,
    today,
    funnel,
    performance,
    monetization,
    quality,
    segmentation,
    customers,
}: Props) {
    const [expDimension, setExpDimension] = useState<SegmentationDimension>(
        segmentation?.dimension ?? "client"
    );
    const [expSegmentation, setExpSegmentation] = useState<OrdersSegmentationResponse | null>(
        segmentation
    );
    const [expSegLoading, setExpSegLoading] = useState(false);

    useEffect(() => {
        if (segmentation && segmentation.dimension === expDimension) {
            setExpSegmentation(segmentation);
            return;
        }

        let cancelled = false;
        setExpSegLoading(true);
        ordersAnalyticsApi.fetchOrdersSegmentation(filters, expDimension)
            .then((next) => {
                if (cancelled) return;
                setExpSegmentation(next);
            })
            .catch(() => {
                if (cancelled) return;
                setExpSegmentation(null);
            })
            .finally(() => {
                if (cancelled) return;
                setExpSegLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [expDimension, filters, segmentation]);

    return (
        <div style={shellStyle}>
            <div style={experimentalBannerStyle}>
                <strong style={bannerLabelStyle}>Experimental</strong>
                <span style={bannerCopyStyle}>
                    Surfaces metrics already computed by the backend but not yet wired into
                    the executive or diagnostic views. Safe to iterate here.
                </span>
            </div>

            <TopKpiStrip summary={summary} monetization={monetization} performance={performance} />

            <DimensionSwitcher
                value={expDimension}
                onChange={setExpDimension}
                loading={expSegLoading}
            />

            <div style={twoColumnGridStyle}>
                <ExecutionRiskCard
                    summary={summary}
                    performance={performance}
                    quality={quality}
                />

                <TopIssuesCard segmentation={expSegmentation} />
            </div>

            <div style={twoColumnGridStyle}>
                <AnomalyDetectionCard trends={trends} />

                <RevenueConcentrationCard segmentation={expSegmentation} />
            </div>

            <div style={twoColumnGridStyle}>
                <BestSegmentsCard segmentation={expSegmentation} />

                <OpportunityScatterCard segmentation={expSegmentation} />
            </div>

            <div style={twoColumnGridStyle}>
                <TrendsExplorerCard trends={trends} />

                <BottleneckIdentifierCard
                    performance={performance}
                    funnel={funnel}
                />
            </div>

            <TodayHourlyVolumeSection today={today} />

            <FunnelSection funnel={funnel} />

            <LatencyStagesSection performance={performance} />

            <SlaHeatmapSection performance={performance} />

            <MonetizationDetailsSection monetization={monetization} />

            <RevenueDistributionSection monetization={monetization} />

            <CustomersSection customers={customers} />
        </div>
    );
}

function TopKpiStrip({
    summary,
    monetization,
    performance,
}: {
    summary: OrdersSummaryResponse | null;
    monetization: OrdersMonetizationResponse | null;
    performance: OrdersPerformanceResponse | null;
}) {
    if (summary === null) {
        return (
            <section style={cardStyle}>
                <header style={sectionHeaderStyle}>
                    <div>
                        <h2 style={sectionTitleStyle}>Top KPI strip</h2>
                        <p style={sectionSubtitleStyle}>
                            Compact KPI overview with inline sparklines.
                        </p>
                    </div>
                </header>
                <EmptyHint label="No summary data for the selected period." />
            </section>
        );
    }

    const kpis: KpiCellProps[] = [
        {
            label: "Total revenue",
            metric: summary.businessRevenue,
            formatter: formatters.formatCurrency,
            tone: deltaTone(summary.businessRevenue.deltaPercentage, "higher-better"),
            sparklineColor: analyticsTheme.colors.success,
        },
        {
            label: "Total volume",
            metric: summary.volume,
            formatter: formatters.formatCurrency,
            tone: deltaTone(summary.volume.deltaPercentage, "higher-better"),
            sparklineColor: analyticsTheme.colors.accentBlue,
        },
        {
            label: "Completed orders",
            metric: summary.completedOrders,
            formatter: formatters.formatNumber,
            tone: deltaTone(summary.completedOrders.deltaPercentage, "higher-better"),
            sparklineColor: analyticsTheme.colors.accentBlue,
        },
        {
            label: "Success rate",
            metric: summary.successRate,
            formatter: formatters.formatPercentage,
            tone: successRateTone(summary.successRate.current),
            sparklineColor: analyticsTheme.colors.success,
        },
        {
            label: "Average ticket",
            metric: summary.averageTicket,
            formatter: formatters.formatCurrency,
            tone: deltaTone(summary.averageTicket.deltaPercentage, "neutral"),
            sparklineColor: analyticsTheme.colors.accentPurple,
        },
        {
            label: "p50 completion",
            metric: summary.completionTime.p50,
            formatter: formatters.formatDuration,
            tone: deltaTone(summary.completionTime.p50.deltaPercentage, "lower-better"),
            sparklineColor: analyticsTheme.colors.accentCyan,
        },
        {
            label: "p95 completion",
            metric: summary.completionTime.p95,
            formatter: formatters.formatDuration,
            tone: latencyToneToKpiTone(latencyTone(summary.completionTime.p95.current)),
            sparklineColor: analyticsTheme.colors.accentCyan,
        },
    ];

    return (
        <section style={cardStyle}>
            <header style={sectionHeaderStyle}>
                <div>
                    <h2 style={sectionTitleStyle}>Top KPI strip</h2>
                    <p style={sectionSubtitleStyle}>
                        Compact KPI overview with inline sparklines from{" "}
                        <code style={inlineCodeStyle}>summary.*.trend</code>.
                    </p>
                </div>
            </header>

            <div style={kpiStripStyle}>
                {kpis.map((kpi) => (
                    <KpiCell key={kpi.label} {...kpi} />
                ))}
                {monetization ? (
                    <KpiCell
                        label="Fee per order"
                        staticValue={formatters.formatCurrency(monetization.feePerOrder)}
                        helper="derived · no period delta"
                        tone="neutral"
                    />
                ) : null}
                {performance ? (
                    <KpiCell
                        label="Slow order rate"
                        staticValue={formatters.formatPercentage(performance.slowOrders.rate)}
                        helper={`${formatters.formatNumber(performance.slowOrders.count)} slow orders`}
                        tone={
                            performance.slowOrders.rate >= 5
                                ? "danger"
                                : performance.slowOrders.rate >= 2
                                  ? "warning"
                                  : "ok"
                        }
                    />
                ) : null}
            </div>
        </section>
    );
}

type KpiCellTone = "ok" | "warning" | "danger" | "neutral";

type KpiCellProps = {
    label: string;
    metric?: KpiMetric;
    staticValue?: string;
    helper?: string;
    formatter?: (value: number) => string;
    tone: KpiCellTone;
    sparklineColor?: string;
};

function KpiCell({
    label,
    metric,
    staticValue,
    helper,
    formatter,
    tone,
    sparklineColor,
}: KpiCellProps) {
    const value =
        staticValue !== undefined
            ? staticValue
            : metric && formatter
              ? formatter(metric.current)
              : "—";
    const deltaPercentage = metric?.deltaPercentage ?? null;
    const trendValues = metric?.trend?.map((point) => point.value) ?? [];

    return (
        <div style={kpiCellStyle}>
            <span style={kpiCellLabelStyle}>{label}</span>
            <strong style={kpiCellValueStyle}>{value}</strong>
            <div style={kpiCellFootStyle}>
                {deltaPercentage !== null ? (
                    <span style={kpiCellDeltaStyle(tone)}>
                        {deltaPercentage >= 0 ? "+" : ""}
                        {deltaPercentage.toFixed(1)}%
                    </span>
                ) : helper ? (
                    <span style={kpiCellHelperStyle}>{helper}</span>
                ) : (
                    <span style={kpiCellHelperStyle}>vs previous period</span>
                )}
                {trendValues.length >= 2 ? (
                    <Sparkline
                        points={trendValues}
                        color={sparklineColor ?? analyticsTheme.colors.accentBlue}
                    />
                ) : null}
            </div>
        </div>
    );
}

function Sparkline({
    points,
    color,
    width = 72,
    height = 22,
}: {
    points: number[];
    color: string;
    width?: number;
    height?: number;
}) {
    if (points.length < 2) {
        return null;
    }
    const min = Math.min(...points);
    const max = Math.max(...points);
    const span = max - min || 1;
    const path = points
        .map((value, index) => {
            const x = (index / (points.length - 1)) * width;
            const y = height - ((value - min) / span) * (height - 2) - 1;
            return `${index === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
        })
        .join(" ");
    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ flex: "0 0 auto" }}>
            <path
                d={path}
                fill="none"
                stroke={color}
                strokeWidth={1.5}
                strokeLinejoin="round"
                strokeLinecap="round"
            />
        </svg>
    );
}

function ExecutionRiskCard({
    summary,
    performance,
    quality,
}: {
    summary: OrdersSummaryResponse | null;
    performance: OrdersPerformanceResponse | null;
    quality: OrdersQualityResponse | null;
}) {
    if (summary === null || performance === null || quality === null) {
        return (
            <section style={cardStyle}>
                <header style={sectionHeaderStyle}>
                    <div>
                        <h2 style={sectionTitleStyle}>Execution risk score</h2>
                        <p style={sectionSubtitleStyle}>
                            Composite 0–100 score derived from success, accuracy and SLA signals.
                        </p>
                    </div>
                </header>
                <EmptyHint label="Need summary, performance and quality data to compute risk." />
            </section>
        );
    }

    const successRate = clamp(summary.successRate.current, 0, 100);
    const feeDevRate = Math.abs(quality.expectedFeeVsActual.deviationRate);
    const amountDevRate = Math.abs(quality.expectedAmountVsReceived.deviationRate);
    const destinationDevRate = Math.abs(quality.promisedVsExecuted.deviationRate);
    const slowRate = clamp(performance.slowOrders.rate, 0, 100);

    const contributions = [
        {
            key: "failure",
            label: "Failure",
            weight: 0.4,
            raw: 100 - successRate,
            normalized: 100 - successRate,
            note: `success ${formatters.formatPercentage(successRate)}`,
        },
        {
            key: "fee",
            label: "Fee deviation",
            weight: 0.2,
            raw: feeDevRate,
            normalized: Math.min(100, feeDevRate * 4),
            note: `dev ${formatters.formatPercentage(feeDevRate)}`,
        },
        {
            key: "amount",
            label: "Amount deviation",
            weight: 0.15,
            raw: amountDevRate,
            normalized: Math.min(100, amountDevRate * 4),
            note: `dev ${formatters.formatPercentage(amountDevRate)}`,
        },
        {
            key: "destination",
            label: "Destination deviation",
            weight: 0.1,
            raw: destinationDevRate,
            normalized: Math.min(100, destinationDevRate * 4),
            note: `dev ${formatters.formatPercentage(destinationDevRate)}`,
        },
        {
            key: "sla",
            label: "Slow order rate",
            weight: 0.15,
            raw: slowRate,
            normalized: slowRate,
            note: `${formatters.formatNumber(performance.slowOrders.count)} slow`,
        },
    ];

    const riskScore = clamp(
        contributions.reduce((sum, c) => sum + c.weight * c.normalized, 0),
        0,
        100
    );
    const severity: KpiCellTone =
        riskScore < 15 ? "ok" : riskScore < 35 ? "warning" : "danger";
    const severityLabel =
        severity === "ok" ? "Stable" : severity === "warning" ? "Watch" : "Critical";
    const severityColor =
        severity === "ok"
            ? analyticsTheme.colors.success
            : severity === "warning"
              ? analyticsTheme.colors.warning
              : analyticsTheme.colors.danger;

    return (
        <section style={cardStyle}>
            <header style={sectionHeaderStyle}>
                <div>
                    <h2 style={sectionTitleStyle}>Execution risk score</h2>
                    <p style={sectionSubtitleStyle}>
                        Composite 0–100. 40% failure · 20% fee dev · 15% amount dev · 10%
                        destination dev · 15% slow orders.
                    </p>
                </div>
            </header>

            <div style={riskMainRowStyle}>
                <div style={riskNumberWrapStyle}>
                    <div style={{ ...riskNumberStyle, color: severityColor }}>
                        {riskScore.toFixed(0)}
                    </div>
                    <span style={{ ...riskSeverityPillStyle, color: severityColor, borderColor: severityColor }}>
                        {severityLabel}
                    </span>
                </div>

                <div style={riskContributionsStyle}>
                    {contributions.map((c) => {
                        const contributionPoints = c.weight * c.normalized;
                        const rawPct = (contributionPoints / riskScore) * 100;
                        const widthPct = clamp(
                            Number.isFinite(rawPct) ? rawPct : 0,
                            0,
                            100
                        );
                        return (
                            <div key={c.key} style={riskRowStyle}>
                                <div style={riskRowHeadStyle}>
                                    <span style={riskRowLabelStyle}>
                                        {c.label}
                                        <small style={riskWeightStyle}>{c.weight * 100}%</small>
                                    </span>
                                    <span style={riskRowValueStyle}>
                                        +{contributionPoints.toFixed(1)} pts
                                        <small style={riskRowNoteStyle}>{c.note}</small>
                                    </span>
                                </div>
                                <div style={riskBarTrackStyle}>
                                    <div
                                        style={{
                                            ...riskBarFillStyle,
                                            width: `${widthPct}%`,
                                            background: severityColor,
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function TopIssuesCard({
    segmentation,
}: {
    segmentation: OrdersSegmentationResponse | null;
}) {
    if (segmentation === null || segmentation.rows.length === 0) {
        return (
            <section style={cardStyle}>
                <header style={sectionHeaderStyle}>
                    <div>
                        <h2 style={sectionTitleStyle}>Top issues by impact</h2>
                        <p style={sectionSubtitleStyle}>
                            Scored ranking of problematic segments from the current segmentation.
                        </p>
                    </div>
                </header>
                <EmptyHint label="No segmentation data for the selected period." />
            </section>
        );
    }

    const scored = segmentation.rows
        .map((row) => {
            const failureRate = row.orders > 0 ? (row.failedOrders / row.orders) * 100 : 0;
            const issueScore =
                0.4 * failureRate +
                0.25 * Math.abs(row.feeDeviationRate) +
                0.2 * Math.abs(row.amountDeviationRate) +
                0.15 * (row.p95TotalTimeMinutes > 60 ? Math.min(100, row.p95TotalTimeMinutes - 60) : 0);
            const impact = issueScore * Math.log10(Math.max(row.volume, 1) + 1);
            const primary = pickPrimaryIssue(row, failureRate);
            return { row, issueScore, impact, primary, failureRate };
        })
        .sort((a, b) => b.impact - a.impact)
        .slice(0, 5);

    return (
        <section style={cardStyle}>
            <header style={sectionHeaderStyle}>
                <div>
                    <h2 style={sectionTitleStyle}>
                        Top issues by impact ·{" "}
                        <span style={dimensionPillStyle}>{segmentation.dimension}</span>
                    </h2>
                    <p style={sectionSubtitleStyle}>
                        Score = 0.40 × failure + 0.25 × fee-dev + 0.20 × amount-dev + 0.15 × p95-excess,
                        weighted by log(volume).
                    </p>
                </div>
            </header>

            <div style={tableScrollStyle}>
                <table style={latencyTableStyle}>
                    <thead>
                        <tr>
                            <th style={latencyHeadCellStyle("left")}>#</th>
                            <th style={latencyHeadCellStyle("left")}>Segment</th>
                            <th style={latencyHeadCellStyle("left")}>Primary</th>
                            <th style={latencyHeadCellStyle("right")}>Orders</th>
                            <th style={latencyHeadCellStyle("right")}>Failures</th>
                            <th style={latencyHeadCellStyle("right")}>Volume</th>
                            <th style={latencyHeadCellStyle("right")}>Impact</th>
                        </tr>
                    </thead>
                    <tbody>
                        {scored.map(({ row, impact, primary, failureRate }, index) => (
                            <tr key={row.segment}>
                                <td style={latencyCellStyle("left")}>
                                    <span style={rankBadgeStyle}>{index + 1}</span>
                                </td>
                                <td style={latencyCellStyle("left")}>
                                    <strong>{row.segment}</strong>
                                </td>
                                <td style={latencyCellStyle("left")}>
                                    <span style={primaryIssueStyle(primary.tone)}>{primary.label}</span>
                                    <small style={primaryNoteStyle}>{primary.note}</small>
                                </td>
                                <td style={latencyCellStyle("right")}>{formatters.formatNumber(row.orders)}</td>
                                <td style={latencyCellStyle("right")}>
                                    {formatters.formatNumber(row.failedOrders)}{" "}
                                    <small style={primaryNoteStyle}>({formatters.formatPercentage(failureRate)})</small>
                                </td>
                                <td style={latencyCellStyle("right")}>{formatters.formatCurrency(row.volume)}</td>
                                <td style={latencyCellStyle("right")}>{impact.toFixed(1)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function DimensionSwitcher({
    value,
    onChange,
    loading,
}: {
    value: SegmentationDimension;
    onChange: (next: SegmentationDimension) => void;
    loading: boolean;
}) {
    return (
        <section style={dimensionSwitcherStyle}>
            <div style={dimensionSwitcherLabelStyle}>
                <strong style={dimensionSwitcherTitleStyle}>Segment by</strong>
                <span style={dimensionSwitcherHintStyle}>
                    affects Top issues + Revenue concentration · local fetch (Diagnostic
                    unchanged)
                    {loading ? " · loading…" : ""}
                </span>
            </div>
            <div style={dimensionChipGroupStyle}>
                {DIMENSION_OPTIONS.map((opt) => {
                    const active = opt.value === value;
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => onChange(opt.value)}
                            style={dimensionChipStyle(active)}
                        >
                            {opt.label}
                        </button>
                    );
                })}
            </div>
        </section>
    );
}

function TodayHourlyVolumeSection({ today }: { today: OrdersTodayResponse | null }) {
    if (today === null || today.hourlyVolume.length === 0) {
        return (
            <section style={cardStyle}>
                <header style={sectionHeaderStyle}>
                    <div>
                        <h2 style={sectionTitleStyle}>Today's hourly volume</h2>
                        <p style={sectionSubtitleStyle}>
                            Intraday volume distribution from <code style={inlineCodeStyle}>today.hourlyVolume</code>.
                        </p>
                    </div>
                </header>
                <EmptyHint label="No intraday data for today." />
            </section>
        );
    }

    const points = today.hourlyVolume;
    const maxValue = Math.max(...points.map((p) => p.value), 1);
    const totalToday = points.reduce((sum, p) => sum + p.value, 0);
    const peakHour = points.reduce(
        (best, p) => (p.value > best.value ? p : best),
        points[0]
    );

    return (
        <section style={cardStyle}>
            <header style={sectionHeaderStyle}>
                <div>
                    <h2 style={sectionTitleStyle}>Today's hourly volume</h2>
                    <p style={sectionSubtitleStyle}>
                        Intraday volume distribution · {today.timezone} · {points.length} buckets.
                    </p>
                </div>
                <div style={anomalyCountersStyle}>
                    <div style={anomalyCounterStyle}>
                        <small>Total today</small>
                        <strong>{formatters.formatCurrency(totalToday)}</strong>
                    </div>
                    <div style={anomalyCounterStyle}>
                        <small>Peak hour</small>
                        <strong>{peakHour.hour}:00</strong>
                    </div>
                </div>
            </header>

            <div style={hourlyChartStyle}>
                {points.map((point) => {
                    const heightPct = (point.value / maxValue) * 100;
                    const isPeak = point.value === peakHour.value && point.value > 0;
                    return (
                        <div key={point.hour} style={hourlyBarColumnStyle}>
                            <div style={hourlyBarTrackStyle}>
                                <div
                                    style={{
                                        ...hourlyBarFillStyle,
                                        height: `${heightPct}%`,
                                        background: isPeak
                                            ? `linear-gradient(180deg, ${analyticsTheme.colors.accentBlue} 0%, ${analyticsTheme.colors.accentPurple} 100%)`
                                            : `linear-gradient(180deg, ${analyticsTheme.colors.accentBlue} 0%, rgba(50,88,216,0.45) 100%)`,
                                    }}
                                    title={`${point.hour}:00 — ${formatters.formatCurrency(point.value)}`}
                                />
                            </div>
                            <span style={hourlyHourLabelStyle}>
                                {point.hour.toString().padStart(2, "0")}
                            </span>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function SlaHeatmapSection({
    performance,
}: {
    performance: OrdersPerformanceResponse | null;
}) {
    type DurationStageKey = "inflow" | "exchange" | "outflow" | "totalOrderTime";

    const stages: { key: DurationStageKey; label: string }[] = [
        { key: "inflow", label: "Created → funded" },
        { key: "exchange", label: "Funded → exchanged" },
        { key: "outflow", label: "Exchanged → completed" },
        { key: "totalOrderTime", label: "End-to-end" },
    ];

    if (performance === null) {
        return (
            <section style={cardStyle}>
                <header style={sectionHeaderStyle}>
                    <div>
                        <h2 style={sectionTitleStyle}>SLA latency heatmap</h2>
                        <p style={sectionSubtitleStyle}>
                            Stages × percentiles, color-coded by duration band.
                        </p>
                    </div>
                </header>
                <EmptyHint label="No performance data." />
            </section>
        );
    }

    return (
        <section style={cardStyle}>
            <header style={sectionHeaderStyle}>
                <div>
                    <h2 style={sectionTitleStyle}>SLA latency heatmap</h2>
                    <p style={sectionSubtitleStyle}>
                        Cells colored by SLA band: green &lt;30min · amber 30–60min · red ≥60min.
                    </p>
                </div>
                <div style={heatmapLegendStyle}>
                    <span style={heatmapLegendItemStyle("ok")}>&lt;30m</span>
                    <span style={heatmapLegendItemStyle("watch")}>30–60m</span>
                    <span style={heatmapLegendItemStyle("breach")}>≥60m</span>
                </div>
            </header>

            <div style={heatmapWrapStyle}>
                <div style={heatmapHeaderStyle}>
                    <span />
                    <span style={heatmapColHeaderStyle}>p50</span>
                    <span style={heatmapColHeaderStyle}>p95</span>
                    <span style={heatmapColHeaderStyle}>max</span>
                </div>
                {stages.map(({ key, label }) => {
                    const stage = performance[key];
                    return (
                        <div key={key} style={heatmapRowStyle}>
                            <span style={heatmapRowLabelStyle}>{label}</span>
                            <HeatmapCell minutes={stage.p50Minutes} />
                            <HeatmapCell minutes={stage.p95Minutes} />
                            <HeatmapCell minutes={stage.maxMinutes} />
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function HeatmapCell({ minutes }: { minutes: number }) {
    const tone = latencyTone(minutes);
    return <span style={heatmapCellStyle(tone)}>{formatters.formatDuration(minutes)}</span>;
}

function RevenueDistributionSection({
    monetization,
}: {
    monetization: OrdersMonetizationResponse | null;
}) {
    if (
        !monetization ||
        (monetization.revenueByOrderSize.length === 0 &&
            monetization.ticketSizeDistribution.length === 0)
    ) {
        return (
            <section style={cardStyle}>
                <header style={sectionHeaderStyle}>
                    <div>
                        <h2 style={sectionTitleStyle}>Revenue distribution by ticket range</h2>
                        <p style={sectionSubtitleStyle}>
                            Revenue and order counts per ticket bucket.
                        </p>
                    </div>
                </header>
                <EmptyHint label="No monetization distribution data." />
            </section>
        );
    }

    const revenueBuckets = monetization.revenueByOrderSize;
    const orderBuckets = monetization.ticketSizeDistribution;
    const totalRevenue = revenueBuckets.reduce((sum, b) => sum + b.businessRevenue, 0);
    const totalOrders = orderBuckets.reduce((sum, b) => sum + b.orders, 0);

    const maxRev = Math.max(...revenueBuckets.map((b) => b.businessRevenue), 1);
    const maxOrders = Math.max(...orderBuckets.map((b) => b.orders), 1);

    const keys = Array.from(
        new Set([
            ...revenueBuckets.map((b) => b.key),
            ...orderBuckets.map((b) => b.key),
        ])
    );

    return (
        <section style={cardStyle}>
            <header style={sectionHeaderStyle}>
                <div>
                    <h2 style={sectionTitleStyle}>Revenue distribution by ticket range</h2>
                    <p style={sectionSubtitleStyle}>
                        Side-by-side revenue and order counts per ticket bucket. Reveals which
                        ranges drive monetization vs which carry volume.
                    </p>
                </div>
                <div style={anomalyCountersStyle}>
                    <div style={anomalyCounterStyle}>
                        <small>Σ revenue</small>
                        <strong>{formatters.formatCurrency(totalRevenue)}</strong>
                    </div>
                    <div style={anomalyCounterStyle}>
                        <small>Σ orders</small>
                        <strong>{formatters.formatNumber(totalOrders)}</strong>
                    </div>
                </div>
            </header>

            <div style={distributionListStyle}>
                {keys.map((key) => {
                    const rev = revenueBuckets.find((b) => b.key === key);
                    const ord = orderBuckets.find((b) => b.key === key);
                    const revValue = rev?.businessRevenue ?? 0;
                    const ordValue = ord?.orders ?? 0;
                    const revPct = (revValue / maxRev) * 100;
                    const ordPct = (ordValue / maxOrders) * 100;
                    const revShare = totalRevenue > 0 ? (revValue / totalRevenue) * 100 : 0;
                    const ordShare = totalOrders > 0 ? (ordValue / totalOrders) * 100 : 0;
                    return (
                        <div key={key} style={distributionRowStyle}>
                            <span style={distributionLabelStyle}>{key}</span>
                            <div style={distributionBarsStyle}>
                                <div style={distributionBarItemStyle}>
                                    <div style={distributionBarHeadStyle}>
                                        <span style={distributionBarLegendStyle(analyticsTheme.colors.accentBlue)}>
                                            Revenue
                                        </span>
                                        <span style={distributionBarValueStyle}>
                                            {formatters.formatCurrency(revValue)}
                                            <small style={topThreePctStyle}>
                                                ({revShare.toFixed(1)}%)
                                            </small>
                                        </span>
                                    </div>
                                    <div style={riskBarTrackStyle}>
                                        <div
                                            style={{
                                                ...riskBarFillStyle,
                                                width: `${revPct}%`,
                                                background: analyticsTheme.colors.accentBlue,
                                            }}
                                        />
                                    </div>
                                </div>
                                <div style={distributionBarItemStyle}>
                                    <div style={distributionBarHeadStyle}>
                                        <span style={distributionBarLegendStyle(analyticsTheme.colors.accentPurple)}>
                                            Orders
                                        </span>
                                        <span style={distributionBarValueStyle}>
                                            {formatters.formatNumber(ordValue)}
                                            <small style={topThreePctStyle}>
                                                ({ordShare.toFixed(1)}%)
                                            </small>
                                        </span>
                                    </div>
                                    <div style={riskBarTrackStyle}>
                                        <div
                                            style={{
                                                ...riskBarFillStyle,
                                                width: `${ordPct}%`,
                                                background: analyticsTheme.colors.accentPurple,
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

type AnomalySignal = {
    seriesKey: AnomalySeriesKey;
    seriesLabel: string;
    timestamp: number;
    label: string;
    observed: number;
    expected: number;
    zScore: number;
    direction: "spike" | "drop";
    severity: KpiCellTone;
    formatter: (value: number) => string;
};

type AnomalySeriesKey =
    | "volume"
    | "businessRevenue"
    | "successRate"
    | "completionTimeP95Minutes";

const ANOMALY_SERIES: {
    key: AnomalySeriesKey;
    label: string;
    formatter: (value: number) => string;
    invertSpikeTone: boolean;
}[] = [
    { key: "volume", label: "Volume", formatter: formatters.formatCurrency, invertSpikeTone: false },
    {
        key: "businessRevenue",
        label: "Business revenue",
        formatter: formatters.formatCurrency,
        invertSpikeTone: false,
    },
    {
        key: "successRate",
        label: "Success rate",
        formatter: formatters.formatPercentage,
        invertSpikeTone: false,
    },
    {
        key: "completionTimeP95Minutes",
        label: "p95 completion time",
        formatter: formatters.formatDuration,
        invertSpikeTone: true,
    },
];

const ANOMALY_WINDOW = 7;
const ANOMALY_Z_THRESHOLD = 2.5;
const ANOMALY_SEVERE_Z = 4.0;

function detectAnomalies(points: TrendsPoint[]): AnomalySignal[] {
    if (points.length < ANOMALY_WINDOW + 1) {
        return [];
    }

    const signals: AnomalySignal[] = [];

    for (const series of ANOMALY_SERIES) {
        for (let i = ANOMALY_WINDOW; i < points.length; i += 1) {
            const window = points.slice(i - ANOMALY_WINDOW, i).map((p) => p[series.key]);
            const sortedWindow = [...window].sort((a, b) => a - b);
            const median = sortedWindow[Math.floor(sortedWindow.length / 2)];
            const deviations = window.map((v) => Math.abs(v - median)).sort((a, b) => a - b);
            const mad = deviations[Math.floor(deviations.length / 2)] || 1e-9;
            const robustStd = mad * 1.4826;
            const observed = points[i][series.key];
            const zScore = (observed - median) / robustStd;
            const absZ = Math.abs(zScore);

            if (absZ < ANOMALY_Z_THRESHOLD) {
                continue;
            }

            const direction: "spike" | "drop" = zScore >= 0 ? "spike" : "drop";
            const isBad =
                (direction === "drop" && !series.invertSpikeTone) ||
                (direction === "spike" && series.invertSpikeTone);

            const severity: KpiCellTone = isBad
                ? absZ >= ANOMALY_SEVERE_Z
                    ? "danger"
                    : "warning"
                : "neutral";

            signals.push({
                seriesKey: series.key,
                seriesLabel: series.label,
                timestamp: points[i].timestamp,
                label: points[i].label,
                observed,
                expected: median,
                zScore,
                direction,
                severity,
                formatter: series.formatter,
            });
        }
    }

    return signals.sort((a, b) => Math.abs(b.zScore) - Math.abs(a.zScore));
}

function AnomalyDetectionCard({ trends }: { trends: OrdersTrendsResponse | null }) {
    if (trends === null || trends.points.length < ANOMALY_WINDOW + 1) {
        return (
            <section style={cardStyle}>
                <header style={sectionHeaderStyle}>
                    <div>
                        <h2 style={sectionTitleStyle}>Anomaly detection</h2>
                        <p style={sectionSubtitleStyle}>
                            Robust z-score (MAD) over a rolling 7-point window across volume,
                            revenue, success rate and p95 time.
                        </p>
                    </div>
                </header>
                <EmptyHint
                    label={
                        !trends
                            ? "No trend data."
                            : `Need at least ${ANOMALY_WINDOW + 1} points to detect anomalies — currently ${trends.points.length}.`
                    }
                />
            </section>
        );
    }

    const signals = detectAnomalies(trends.points);
    const highImpact = signals.filter((s) => Math.abs(s.zScore) >= ANOMALY_SEVERE_Z).length;
    const top = signals.slice(0, 6);

    return (
        <section style={cardStyle}>
            <header style={sectionHeaderStyle}>
                <div>
                    <h2 style={sectionTitleStyle}>Anomaly detection</h2>
                    <p style={sectionSubtitleStyle}>
                        z-score threshold {ANOMALY_Z_THRESHOLD} on rolling 7-pt MAD window.
                        Drops in volume/revenue/success and spikes in p95 flagged as bad.
                    </p>
                </div>
                <div style={anomalyCountersStyle}>
                    <div style={anomalyCounterStyle}>
                        <small>Total</small>
                        <strong>{signals.length}</strong>
                    </div>
                    <div style={anomalyCounterStyle}>
                        <small>High impact</small>
                        <strong style={{ color: analyticsTheme.colors.danger }}>
                            {highImpact}
                        </strong>
                    </div>
                </div>
            </header>

            {top.length === 0 ? (
                <EmptyHint label="No anomalies detected in the current period." />
            ) : (
                <div style={anomalyListStyle}>
                    {top.map((signal) => {
                        const delta = signal.observed - signal.expected;
                        const deltaPct =
                            signal.expected !== 0
                                ? (delta / Math.abs(signal.expected)) * 100
                                : null;
                        return (
                            <div
                                key={`${signal.seriesKey}-${signal.timestamp}`}
                                style={anomalyRowStyle(signal.severity)}
                            >
                                <div style={anomalyRowHeadStyle}>
                                    <span style={anomalyBadgeStyle(signal.severity)}>
                                        {signal.direction === "spike" ? "↑" : "↓"}{" "}
                                        {signal.seriesLabel}
                                    </span>
                                    <span style={anomalyDateStyle}>{signal.label}</span>
                                </div>
                                <div style={anomalyRowBodyStyle}>
                                    <span>
                                        <small style={anomalyLabelStyle}>Observed</small>
                                        <strong>{signal.formatter(signal.observed)}</strong>
                                    </span>
                                    <span>
                                        <small style={anomalyLabelStyle}>Expected</small>
                                        <strong>{signal.formatter(signal.expected)}</strong>
                                    </span>
                                    <span>
                                        <small style={anomalyLabelStyle}>Δ</small>
                                        <strong>
                                            {deltaPct === null
                                                ? signal.formatter(delta)
                                                : `${deltaPct >= 0 ? "+" : ""}${deltaPct.toFixed(1)}%`}
                                        </strong>
                                    </span>
                                    <span>
                                        <small style={anomalyLabelStyle}>z-score</small>
                                        <strong>{signal.zScore.toFixed(2)}</strong>
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
}

function RevenueConcentrationCard({
    segmentation,
}: {
    segmentation: OrdersSegmentationResponse | null;
}) {
    if (segmentation === null || segmentation.rows.length === 0) {
        return (
            <section style={cardStyle}>
                <header style={sectionHeaderStyle}>
                    <div>
                        <h2 style={sectionTitleStyle}>Revenue concentration</h2>
                        <p style={sectionSubtitleStyle}>
                            Herfindahl–Hirschman index + top-N share derived from segmentation.
                        </p>
                    </div>
                </header>
                <EmptyHint label="No segmentation data." />
            </section>
        );
    }

    const sorted = [...segmentation.rows]
        .filter((row) => row.businessRevenue > 0)
        .sort((a, b) => b.businessRevenue - a.businessRevenue);
    const totalRevenue = sorted.reduce((sum, row) => sum + row.businessRevenue, 0);

    if (totalRevenue <= 0) {
        return (
            <section style={cardStyle}>
                <header style={sectionHeaderStyle}>
                    <div>
                        <h2 style={sectionTitleStyle}>Revenue concentration</h2>
                        <p style={sectionSubtitleStyle}>
                            Herfindahl–Hirschman index + top-N share.
                        </p>
                    </div>
                </header>
                <EmptyHint label="No revenue in the selected period." />
            </section>
        );
    }

    const hhi = sorted.reduce((sum, row) => {
        const share = row.businessRevenue / totalRevenue;
        return sum + share * share;
    }, 0) * 10000;

    const top1Share = (sorted[0].businessRevenue / totalRevenue) * 100;
    const top3Share =
        (sorted.slice(0, 3).reduce((s, r) => s + r.businessRevenue, 0) / totalRevenue) * 100;
    const top5Share =
        (sorted.slice(0, 5).reduce((s, r) => s + r.businessRevenue, 0) / totalRevenue) * 100;

    const interpretation: { label: string; tone: KpiCellTone } =
        hhi < 1500
            ? { label: "Diversified", tone: "ok" }
            : hhi < 2500
              ? { label: "Moderately concentrated", tone: "warning" }
              : { label: "Highly concentrated", tone: "danger" };

    const interpretationColor =
        interpretation.tone === "ok"
            ? analyticsTheme.colors.success
            : interpretation.tone === "warning"
              ? analyticsTheme.colors.warning
              : analyticsTheme.colors.danger;

    const topThree = sorted.slice(0, 3);

    return (
        <section style={cardStyle}>
            <header style={sectionHeaderStyle}>
                <div>
                    <h2 style={sectionTitleStyle}>
                        Revenue concentration ·{" "}
                        <span style={dimensionPillStyle}>{segmentation.dimension}</span>
                    </h2>
                    <p style={sectionSubtitleStyle}>
                        HHI: Σ(share²) × 10 000. Bands: &lt;1500 diversified, 1500–2500 moderate,
                        &gt;2500 highly concentrated.
                    </p>
                </div>
            </header>

            <div style={concentrationGridStyle}>
                <div style={hhiBlockStyle}>
                    <div style={{ ...hhiNumberStyle, color: interpretationColor }}>
                        {Math.round(hhi).toLocaleString("en-US")}
                    </div>
                    <span
                        style={{
                            ...hhiBadgeStyle,
                            color: interpretationColor,
                            borderColor: interpretationColor,
                        }}
                    >
                        {interpretation.label}
                    </span>
                </div>

                <div style={shareRowsStyle}>
                    <ShareRow label="Top 1 share" pct={top1Share} color={analyticsTheme.colors.accentBlue} />
                    <ShareRow label="Top 3 share" pct={top3Share} color={analyticsTheme.colors.accentPurple} />
                    <ShareRow label="Top 5 share" pct={top5Share} color={analyticsTheme.colors.accentCyan} />
                </div>
            </div>

            <div style={topThreeListStyle}>
                {topThree.map((row, index) => (
                    <div key={row.segment} style={topThreeRowStyle}>
                        <span style={topThreeRankStyle}>#{index + 1}</span>
                        <span style={topThreeNameStyle}>{row.segment}</span>
                        <span style={topThreeShareStyle}>
                            {formatters.formatCurrency(row.businessRevenue)}{" "}
                            <small style={topThreePctStyle}>
                                ({((row.businessRevenue / totalRevenue) * 100).toFixed(1)}%)
                            </small>
                        </span>
                    </div>
                ))}
            </div>
        </section>
    );
}

function ShareRow({
    label,
    pct,
    color,
}: {
    label: string;
    pct: number;
    color: string;
}) {
    const widthPct = clamp(pct, 0, 100);
    return (
        <div style={shareRowStyle}>
            <div style={shareRowHeadStyle}>
                <span style={shareRowLabelStyle}>{label}</span>
                <span style={shareRowValueStyle}>{pct.toFixed(1)}%</span>
            </div>
            <div style={riskBarTrackStyle}>
                <div style={{ ...riskBarFillStyle, width: `${widthPct}%`, background: color }} />
            </div>
        </div>
    );
}

type TrendsMetricKey =
    | "volume"
    | "businessRevenue"
    | "completedOrders"
    | "successRate"
    | "completionTimeP95Minutes";

const TRENDS_METRICS: {
    key: TrendsMetricKey;
    label: string;
    color: string;
    formatter: (value: number) => string;
    invertSpikeTone: boolean;
}[] = [
    {
        key: "volume",
        label: "Volume",
        color: analyticsTheme.colors.accentBlue,
        formatter: formatters.formatCurrency,
        invertSpikeTone: false,
    },
    {
        key: "businessRevenue",
        label: "Revenue",
        color: analyticsTheme.colors.success,
        formatter: formatters.formatCurrency,
        invertSpikeTone: false,
    },
    {
        key: "completedOrders",
        label: "Completed",
        color: analyticsTheme.colors.accentPurple,
        formatter: formatters.formatNumber,
        invertSpikeTone: false,
    },
    {
        key: "successRate",
        label: "Success",
        color: analyticsTheme.colors.accentCyan,
        formatter: formatters.formatPercentage,
        invertSpikeTone: false,
    },
    {
        key: "completionTimeP95Minutes",
        label: "p95 time",
        color: analyticsTheme.colors.warning,
        formatter: formatters.formatDuration,
        invertSpikeTone: true,
    },
];

function TrendsExplorerCard({ trends }: { trends: OrdersTrendsResponse | null }) {
    const [metric, setMetric] = useState<TrendsMetricKey>("volume");

    if (trends === null || trends.points.length < 2) {
        return (
            <section style={cardStyle}>
                <header style={sectionHeaderStyle}>
                    <div>
                        <h2 style={sectionTitleStyle}>Trends explorer</h2>
                        <p style={sectionSubtitleStyle}>
                            Single-series view with anomaly markers overlaid.
                        </p>
                    </div>
                </header>
                <EmptyHint label="Not enough trend points to render." />
            </section>
        );
    }

    const config = TRENDS_METRICS.find((m) => m.key === metric) ?? TRENDS_METRICS[0];
    const points = trends.points;
    const values = points.map((p) => p[metric]);

    const allSignals = detectAnomalies(points);
    const signalsForMetric = new Map<number, (typeof allSignals)[number]>();
    for (const signal of allSignals) {
        if (signal.seriesKey === metric) {
            signalsForMetric.set(signal.timestamp, signal);
        }
    }

    const viewportWidth = 480;
    const viewportHeight = 200;
    const margin = { top: 16, right: 14, bottom: 28, left: 56 };
    const plotWidth = viewportWidth - margin.left - margin.right;
    const plotHeight = viewportHeight - margin.top - margin.bottom;

    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const pad = span * 0.08;
    const yMin = min - pad;
    const yMax = max + pad;
    const ySpan = yMax - yMin;

    const xAt = (index: number) =>
        points.length === 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth;
    const yAt = (value: number) => plotHeight - ((value - yMin) / ySpan) * plotHeight;

    const linePath = values
        .map((v, i) => `${i === 0 ? "M" : "L"} ${xAt(i).toFixed(1)} ${yAt(v).toFixed(1)}`)
        .join(" ");
    const areaPath = `${linePath} L ${xAt(values.length - 1).toFixed(1)} ${plotHeight} L ${xAt(0).toFixed(1)} ${plotHeight} Z`;

    const yTicks = 4;
    const tickValues = Array.from({ length: yTicks + 1 }, (_, i) => yMin + (ySpan * i) / yTicks);

    const firstLabel = points[0]?.label ?? "";
    const lastLabel = points[points.length - 1]?.label ?? "";
    const midIndex = Math.floor(points.length / 2);
    const midLabel = points[midIndex]?.label ?? "";

    return (
        <section style={cardStyle}>
            <header style={sectionHeaderStyle}>
                <div>
                    <h2 style={sectionTitleStyle}>Trends explorer</h2>
                    <p style={sectionSubtitleStyle}>
                        Single-series chart with anomalies overlaid (red dots).{" "}
                        {signalsForMetric.size > 0
                            ? `${signalsForMetric.size} anomaly point${signalsForMetric.size === 1 ? "" : "s"} on this series.`
                            : "No anomalies for this series."}
                    </p>
                </div>
                <div style={dimensionChipGroupStyle}>
                    {TRENDS_METRICS.map((opt) => (
                        <button
                            key={opt.key}
                            type="button"
                            onClick={() => setMetric(opt.key)}
                            style={dimensionChipStyle(opt.key === metric)}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </header>

            <div style={scatterWrapStyle}>
                <svg
                    width="100%"
                    height={viewportHeight}
                    viewBox={`0 0 ${viewportWidth} ${viewportHeight}`}
                    preserveAspectRatio="xMidYMid meet"
                    style={{ display: "block" }}
                >
                    <defs>
                        <linearGradient id="trendsExplorerGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={config.color} stopOpacity={0.24} />
                            <stop offset="100%" stopColor={config.color} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <g transform={`translate(${margin.left} ${margin.top})`}>
                        {tickValues.map((tick, i) => (
                            <g key={i}>
                                <line
                                    x1={0}
                                    x2={plotWidth}
                                    y1={yAt(tick)}
                                    y2={yAt(tick)}
                                    stroke="rgba(199,213,229,.5)"
                                    strokeDasharray={i === 0 ? "0" : "3 3"}
                                />
                                <text
                                    x={-8}
                                    y={yAt(tick)}
                                    fontSize={10}
                                    fill={analyticsTheme.colors.textMuted}
                                    textAnchor="end"
                                    dominantBaseline="middle"
                                    fontWeight={600}
                                >
                                    {config.formatter(tick)}
                                </text>
                            </g>
                        ))}

                        <path d={areaPath} fill="url(#trendsExplorerGradient)" />
                        <path
                            d={linePath}
                            fill="none"
                            stroke={config.color}
                            strokeWidth={2.2}
                            strokeLinejoin="round"
                            strokeLinecap="round"
                        />

                        {values.map((v, i) => {
                            const signal = signalsForMetric.get(points[i].timestamp);
                            if (signal === undefined) return null;
                            const tone =
                                signal.severity === "danger"
                                    ? analyticsTheme.colors.danger
                                    : signal.severity === "warning"
                                      ? analyticsTheme.colors.warning
                                      : analyticsTheme.colors.textMuted;
                            return (
                                <g key={i}>
                                    <title>
                                        {`${points[i].label}\n${config.label}: ${config.formatter(v)}\nExpected: ${config.formatter(signal.expected)}\nz-score: ${signal.zScore.toFixed(2)}`}
                                    </title>
                                    <circle
                                        cx={xAt(i)}
                                        cy={yAt(v)}
                                        r={5}
                                        fill={tone}
                                        stroke="#FFFFFF"
                                        strokeWidth={2}
                                    />
                                </g>
                            );
                        })}

                        <line
                            x1={0}
                            x2={plotWidth}
                            y1={plotHeight}
                            y2={plotHeight}
                            stroke={analyticsTheme.colors.borderStrong}
                        />
                        <text
                            x={0}
                            y={plotHeight + 18}
                            fontSize={10}
                            fill={analyticsTheme.colors.textMuted}
                            textAnchor="start"
                            fontWeight={600}
                        >
                            {firstLabel}
                        </text>
                        <text
                            x={plotWidth / 2}
                            y={plotHeight + 18}
                            fontSize={10}
                            fill={analyticsTheme.colors.textMuted}
                            textAnchor="middle"
                            fontWeight={600}
                        >
                            {midLabel}
                        </text>
                        <text
                            x={plotWidth}
                            y={plotHeight + 18}
                            fontSize={10}
                            fill={analyticsTheme.colors.textMuted}
                            textAnchor="end"
                            fontWeight={600}
                        >
                            {lastLabel}
                        </text>
                    </g>
                </svg>
            </div>
        </section>
    );
}

function BottleneckIdentifierCard({
    performance,
    funnel,
}: {
    performance: OrdersPerformanceResponse | null;
    funnel: OrdersFunnelResponse | null;
}) {
    if (performance === null) {
        return (
            <section style={cardStyle}>
                <header style={sectionHeaderStyle}>
                    <div>
                        <h2 style={sectionTitleStyle}>Bottleneck identifier</h2>
                        <p style={sectionSubtitleStyle}>
                            Cross-references stage latency p95 with funnel drop-off to identify
                            where orders stall.
                        </p>
                    </div>
                </header>
                <EmptyHint label="No performance data." />
            </section>
        );
    }

    type DurationStageKey2 = "inflow" | "exchange" | "outflow";

    const stageMappings: {
        perfKey: DurationStageKey2;
        label: string;
        funnelLabels: string[];
    }[] = [
        {
            perfKey: "inflow",
            label: "Funding",
            funnelLabels: ["funded", "funding", "funds received"],
        },
        {
            perfKey: "exchange",
            label: "Exchange",
            funnelLabels: ["exchange", "exchanged"],
        },
        {
            perfKey: "outflow",
            label: "Payout",
            funnelLabels: ["withdraw", "withdrawn", "withdrawal"],
        },
    ];

    const totalEndToEndP95 = performance.totalOrderTime.p95Minutes || 1;

    const stages = stageMappings.map(({ perfKey, label, funnelLabels }) => {
        const perfMetric = performance[perfKey];
        const matchingFunnelStage = funnel?.stages.find((s) =>
            funnelLabels.some((needle) =>
                s.label.toLowerCase().includes(needle) || s.key.toLowerCase().includes(needle)
            )
        );
        const dropOffRate = matchingFunnelStage?.dropOffRate ?? 0;

        const latencyShare = clamp((perfMetric.p95Minutes / totalEndToEndP95) * 100, 0, 100);
        const bottleneckScore = 0.6 * latencyShare + 0.4 * dropOffRate;

        return {
            label,
            perfKey,
            p95Minutes: perfMetric.p95Minutes,
            p50Minutes: perfMetric.p50Minutes,
            count: perfMetric.count,
            dropOffRate,
            latencyShare,
            bottleneckScore,
        };
    });

    const sorted = [...stages].sort((a, b) => b.bottleneckScore - a.bottleneckScore);
    const worst = sorted[0];

    const worstTone: KpiCellTone =
        worst.bottleneckScore >= 60
            ? "danger"
            : worst.bottleneckScore >= 35
              ? "warning"
              : "ok";
    const worstColor =
        worstTone === "danger"
            ? analyticsTheme.colors.danger
            : worstTone === "warning"
              ? analyticsTheme.colors.warning
              : analyticsTheme.colors.success;

    return (
        <section style={cardStyle}>
            <header style={sectionHeaderStyle}>
                <div>
                    <h2 style={sectionTitleStyle}>Bottleneck identifier</h2>
                    <p style={sectionSubtitleStyle}>
                        Score = 0.6 × (stage p95 ÷ end-to-end p95) + 0.4 × funnel drop-off. Higher
                        means the stage drives more of the total time + losses.
                    </p>
                </div>
            </header>

            <div style={bottleneckHeroStyle}>
                <div
                    style={{
                        ...bottleneckHeroIconStyle,
                        background: `${worstColor}1a`,
                        borderColor: worstColor,
                    }}
                >
                    <span style={{ ...bottleneckHeroLabelStyle, color: worstColor }}>
                        Bottleneck
                    </span>
                    <strong style={bottleneckHeroNameStyle}>{worst.label}</strong>
                </div>
                <div style={bottleneckHeroMetaStyle}>
                    <div>
                        <small style={anomalyLabelStyle}>Stage p95</small>
                        <strong>{formatters.formatDuration(worst.p95Minutes)}</strong>
                    </div>
                    <div>
                        <small style={anomalyLabelStyle}>Share of E2E</small>
                        <strong>{worst.latencyShare.toFixed(0)}%</strong>
                    </div>
                    <div>
                        <small style={anomalyLabelStyle}>Funnel drop</small>
                        <strong>{formatters.formatPercentage(worst.dropOffRate)}</strong>
                    </div>
                    <div>
                        <small style={anomalyLabelStyle}>Bottleneck score</small>
                        <strong style={{ color: worstColor }}>
                            {worst.bottleneckScore.toFixed(0)}
                        </strong>
                    </div>
                </div>
            </div>

            <div style={bottleneckListStyle}>
                {sorted.map((stage, index) => {
                    const widthPct = clamp(stage.bottleneckScore, 0, 100);
                    const isWorst = index === 0;
                    return (
                        <div key={stage.perfKey} style={bottleneckRowStyle}>
                            <div style={bottleneckRowHeadStyle}>
                                <span style={bottleneckRowLabelStyle}>
                                    {isWorst ? "▲ " : ""}
                                    {stage.label}
                                </span>
                                <span style={bottleneckRowValueStyle}>
                                    {stage.bottleneckScore.toFixed(0)}
                                    <small style={bottleneckRowNoteStyle}>
                                        p95 {formatters.formatDuration(stage.p95Minutes)} · drop{" "}
                                        {formatters.formatPercentage(stage.dropOffRate)}
                                    </small>
                                </span>
                            </div>
                            <div style={riskBarTrackStyle}>
                                <div
                                    style={{
                                        ...riskBarFillStyle,
                                        width: `${widthPct}%`,
                                        background: isWorst
                                            ? worstColor
                                            : analyticsTheme.colors.borderStrong,
                                    }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function BestSegmentsCard({
    segmentation,
}: {
    segmentation: OrdersSegmentationResponse | null;
}) {
    if (segmentation === null || segmentation.rows.length === 0) {
        return (
            <section style={cardStyle}>
                <header style={sectionHeaderStyle}>
                    <div>
                        <h2 style={sectionTitleStyle}>Healthiest segments</h2>
                        <p style={sectionSubtitleStyle}>
                            Positive ranking — high success, low deviation, fast p95.
                        </p>
                    </div>
                </header>
                <EmptyHint label="No segmentation data." />
            </section>
        );
    }

    const scored = segmentation.rows
        .filter((row) => row.orders > 0)
        .map((row) => {
            const failureRate = (row.failedOrders / row.orders) * 100;
            const successScore = row.successRate;
            const accuracyScore =
                100 - (Math.abs(row.feeDeviationRate) + Math.abs(row.amountDeviationRate)) / 2;
            const latencyScore =
                row.p95TotalTimeMinutes <= 30
                    ? 100
                    : row.p95TotalTimeMinutes <= 60
                      ? 80
                      : row.p95TotalTimeMinutes <= 120
                        ? 50
                        : 20;
            const reliabilityScore = Math.max(0, 100 - failureRate * 4);
            const healthScore = clamp(
                0.35 * successScore +
                    0.25 * accuracyScore +
                    0.2 * latencyScore +
                    0.2 * reliabilityScore,
                0,
                100
            );
            const impactWeight = Math.log10(Math.max(row.volume, 1) + 1);
            const weightedHealth = healthScore * impactWeight;
            return { row, healthScore, weightedHealth };
        })
        .sort((a, b) => b.weightedHealth - a.weightedHealth)
        .slice(0, 5);

    return (
        <section style={cardStyle}>
            <header style={sectionHeaderStyle}>
                <div>
                    <h2 style={sectionTitleStyle}>
                        Healthiest segments ·{" "}
                        <span style={dimensionPillStyle}>{segmentation.dimension}</span>
                    </h2>
                    <p style={sectionSubtitleStyle}>
                        Health = 0.35·success + 0.25·accuracy + 0.20·latency + 0.20·reliability,
                        weighted by log(volume).
                    </p>
                </div>
            </header>

            <div style={bestSegmentsListStyle}>
                {scored.map(({ row, healthScore }, index) => {
                    const tone: KpiCellTone =
                        healthScore >= 90 ? "ok" : healthScore >= 70 ? "neutral" : "warning";
                    const toneColor =
                        tone === "ok"
                            ? analyticsTheme.colors.success
                            : tone === "warning"
                              ? analyticsTheme.colors.warning
                              : analyticsTheme.colors.textSecondary;
                    return (
                        <div key={row.segment} style={bestSegmentRowStyle}>
                            <span style={rankBadgeStyle}>{index + 1}</span>
                            <div style={bestSegmentBodyStyle}>
                                <div style={bestSegmentHeadStyle}>
                                    <strong style={bestSegmentNameStyle}>{row.segment}</strong>
                                    <strong style={{ ...bestSegmentScoreStyle, color: toneColor }}>
                                        {healthScore.toFixed(0)}
                                    </strong>
                                </div>
                                <div style={bestSegmentMetaStyle}>
                                    <span>
                                        <small style={anomalyLabelStyle}>Success</small>
                                        <strong>{formatters.formatPercentage(row.successRate)}</strong>
                                    </span>
                                    <span>
                                        <small style={anomalyLabelStyle}>p95</small>
                                        <strong>{formatters.formatDuration(row.p95TotalTimeMinutes)}</strong>
                                    </span>
                                    <span>
                                        <small style={anomalyLabelStyle}>Volume</small>
                                        <strong>{formatters.formatCurrency(row.volume)}</strong>
                                    </span>
                                    <span>
                                        <small style={anomalyLabelStyle}>Revenue</small>
                                        <strong>{formatters.formatCurrency(row.businessRevenue)}</strong>
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function OpportunityScatterCard({
    segmentation,
}: {
    segmentation: OrdersSegmentationResponse | null;
}) {
    if (segmentation === null || segmentation.rows.length === 0) {
        return (
            <section style={cardStyle}>
                <header style={sectionHeaderStyle}>
                    <div>
                        <h2 style={sectionTitleStyle}>Opportunity scatter</h2>
                        <p style={sectionSubtitleStyle}>
                            Volume × success rate, size by orders, color by failure.
                        </p>
                    </div>
                </header>
                <EmptyHint label="No segmentation data." />
            </section>
        );
    }

    const rows = segmentation.rows.filter((row) => row.orders > 0 && row.volume > 0);
    if (rows.length === 0) {
        return (
            <section style={cardStyle}>
                <header style={sectionHeaderStyle}>
                    <div>
                        <h2 style={sectionTitleStyle}>Opportunity scatter</h2>
                    </div>
                </header>
                <EmptyHint label="No segments with traffic." />
            </section>
        );
    }

    const viewportWidth = 480;
    const viewportHeight = 220;
    const margin = { top: 12, right: 12, bottom: 28, left: 44 };
    const plotWidth = viewportWidth - margin.left - margin.right;
    const plotHeight = viewportHeight - margin.top - margin.bottom;

    const maxVolume = Math.max(...rows.map((r) => r.volume));
    const maxOrders = Math.max(...rows.map((r) => r.orders));
    const minVolume = Math.min(...rows.map((r) => r.volume));

    const xLog = (v: number) => {
        const span = Math.log10(maxVolume + 1) - Math.log10(minVolume + 1) || 1;
        return ((Math.log10(v + 1) - Math.log10(minVolume + 1)) / span) * plotWidth;
    };
    const yPos = (success: number) => plotHeight - (clamp(success, 0, 100) / 100) * plotHeight;
    const radius = (orders: number) => {
        const min = 4;
        const max = 14;
        const ratio = Math.sqrt(orders / Math.max(maxOrders, 1));
        return min + ratio * (max - min);
    };
    const dotColor = (row: SegmentationRow): string => {
        const failureRate = (row.failedOrders / Math.max(row.orders, 1)) * 100;
        if (failureRate >= 10) return analyticsTheme.colors.danger;
        if (failureRate >= 5) return analyticsTheme.colors.warning;
        if (row.successRate >= 95) return analyticsTheme.colors.success;
        return analyticsTheme.colors.accentBlue;
    };

    const successGridLines = [25, 50, 75, 95];

    return (
        <section style={cardStyle}>
            <header style={sectionHeaderStyle}>
                <div>
                    <h2 style={sectionTitleStyle}>
                        Opportunity scatter ·{" "}
                        <span style={dimensionPillStyle}>{segmentation.dimension}</span>
                    </h2>
                    <p style={sectionSubtitleStyle}>
                        X · log(volume), Y · success rate, dot size · log(orders), color · failure
                        severity. Bottom-right = high volume + low success (worst).
                    </p>
                </div>
            </header>

            <div style={scatterWrapStyle}>
                <svg
                    width="100%"
                    height={viewportHeight}
                    viewBox={`0 0 ${viewportWidth} ${viewportHeight}`}
                    preserveAspectRatio="xMidYMid meet"
                    style={{ display: "block" }}
                >
                    <g transform={`translate(${margin.left} ${margin.top})`}>
                        {successGridLines.map((pct) => (
                            <g key={pct}>
                                <line
                                    x1={0}
                                    x2={plotWidth}
                                    y1={yPos(pct)}
                                    y2={yPos(pct)}
                                    stroke="rgba(199,213,229,.55)"
                                    strokeDasharray="3 3"
                                />
                                <text
                                    x={-8}
                                    y={yPos(pct)}
                                    fontSize={10}
                                    fill={analyticsTheme.colors.textMuted}
                                    textAnchor="end"
                                    dominantBaseline="middle"
                                    fontWeight={600}
                                >
                                    {pct}%
                                </text>
                            </g>
                        ))}
                        <line
                            x1={0}
                            x2={plotWidth}
                            y1={plotHeight}
                            y2={plotHeight}
                            stroke={analyticsTheme.colors.borderStrong}
                        />
                        <line
                            x1={0}
                            x2={0}
                            y1={0}
                            y2={plotHeight}
                            stroke={analyticsTheme.colors.borderStrong}
                        />
                        <text
                            x={plotWidth / 2}
                            y={plotHeight + 20}
                            fontSize={10}
                            fill={analyticsTheme.colors.textMuted}
                            textAnchor="middle"
                            fontWeight={700}
                            letterSpacing={0.6}
                        >
                            VOLUME (log scale) →
                        </text>
                        <text
                            x={-30}
                            y={plotHeight / 2}
                            fontSize={10}
                            fill={analyticsTheme.colors.textMuted}
                            textAnchor="middle"
                            transform={`rotate(-90 ${-30} ${plotHeight / 2})`}
                            fontWeight={700}
                            letterSpacing={0.6}
                        >
                            SUCCESS RATE
                        </text>
                        {rows.map((row) => {
                            const cx = xLog(row.volume);
                            const cy = yPos(row.successRate);
                            const r = radius(row.orders);
                            const color = dotColor(row);
                            return (
                                <g key={row.segment}>
                                    <title>
                                        {`${row.segment}\nVolume: ${formatters.formatCurrency(row.volume)}\nSuccess: ${formatters.formatPercentage(row.successRate)}\nOrders: ${formatters.formatNumber(row.orders)}\nFailures: ${formatters.formatNumber(row.failedOrders)}`}
                                    </title>
                                    <circle
                                        cx={cx}
                                        cy={cy}
                                        r={r}
                                        fill={color}
                                        fillOpacity={0.7}
                                        stroke="#FFFFFF"
                                        strokeWidth={1.5}
                                    />
                                </g>
                            );
                        })}
                    </g>
                </svg>

                <div style={scatterLegendStyle}>
                    <LegendDot color={analyticsTheme.colors.danger} label="≥10% failure" />
                    <LegendDot color={analyticsTheme.colors.warning} label="5–10% failure" />
                    <LegendDot color={analyticsTheme.colors.success} label="≥95% success" />
                    <LegendDot color={analyticsTheme.colors.accentBlue} label="other" />
                </div>
            </div>
        </section>
    );
}

function LegendDot({ color, label }: { color: string; label: string }) {
    return (
        <span style={legendDotWrapStyle}>
            <span style={{ ...legendDotMarkerStyle, background: color }} />
            {label}
        </span>
    );
}

type PrimaryIssue = {
    label: string;
    note: string;
    tone: KpiCellTone;
};

function pickPrimaryIssue(row: SegmentationRow, failureRate: number): PrimaryIssue {
    const candidates: PrimaryIssue[] = [];

    if (failureRate > 0) {
        candidates.push({
            label: "Failure",
            note: formatters.formatPercentage(failureRate),
            tone: failureRate >= 10 ? "danger" : failureRate >= 5 ? "warning" : "neutral",
        });
    }

    if (Math.abs(row.feeDeviationRate) > 0) {
        candidates.push({
            label: "Fee deviation",
            note: formatters.formatPercentage(Math.abs(row.feeDeviationRate)),
            tone:
                Math.abs(row.feeDeviationRate) >= 10
                    ? "danger"
                    : Math.abs(row.feeDeviationRate) >= 5
                      ? "warning"
                      : "neutral",
        });
    }

    if (Math.abs(row.amountDeviationRate) > 0) {
        candidates.push({
            label: "Amount deviation",
            note: formatters.formatPercentage(Math.abs(row.amountDeviationRate)),
            tone:
                Math.abs(row.amountDeviationRate) >= 10
                    ? "danger"
                    : Math.abs(row.amountDeviationRate) >= 5
                      ? "warning"
                      : "neutral",
        });
    }

    if (row.p95TotalTimeMinutes > 60) {
        candidates.push({
            label: "Slow p95",
            note: formatters.formatDuration(row.p95TotalTimeMinutes),
            tone: row.p95TotalTimeMinutes > 120 ? "danger" : "warning",
        });
    }

    if (candidates.length === 0) {
        return { label: "Healthy", note: "no severe signal", tone: "ok" };
    }

    const toneOrder: Record<KpiCellTone, number> = { danger: 3, warning: 2, neutral: 1, ok: 0 };
    return candidates.sort((a, b) => toneOrder[b.tone] - toneOrder[a.tone])[0];
}

function clamp(value: number, min: number, max: number) {
    return Math.max(min, Math.min(max, value));
}

function deltaTone(
    deltaPercentage: number | null,
    direction: "higher-better" | "lower-better" | "neutral"
): KpiCellTone {
    if (deltaPercentage === null || direction === "neutral") return "neutral";
    if (direction === "higher-better") {
        if (deltaPercentage >= 5) return "ok";
        if (deltaPercentage <= -5) return "danger";
        return "neutral";
    }
    if (deltaPercentage <= -5) return "ok";
    if (deltaPercentage >= 5) return "danger";
    return "neutral";
}

function successRateTone(value: number): KpiCellTone {
    if (value >= 95) return "ok";
    if (value >= 85) return "warning";
    return "danger";
}

function latencyToneToKpiTone(tone: LatencyTone): KpiCellTone {
    if (tone === "ok") return "ok";
    if (tone === "watch") return "warning";
    return "danger";
}

function FunnelSection({ funnel }: { funnel: OrdersFunnelResponse | null }) {
    return (
        <section style={cardStyle}>
            <header style={sectionHeaderStyle}>
                <div>
                    <h2 style={sectionTitleStyle}>Order funnel</h2>
                    <p style={sectionSubtitleStyle}>
                        Conversion rate at each operational stage. Drop-off highlights where
                        orders stall.
                    </p>
                </div>
                {funnel ? (
                    <div style={stuckPillStyle}>
                        {formatters.formatNumber(funnel.stuckOrders)} stuck
                    </div>
                ) : null}
            </header>

            {!funnel || funnel.stages.length === 0 ? (
                <EmptyHint label="No funnel data for the selected period." />
            ) : (
                <div style={funnelListStyle}>
                    {funnel.stages.map((stage, index) => {
                        const passPct = Math.max(0, Math.min(100, stage.passThroughRate));
                        const dropPct = Math.max(0, Math.min(100, stage.dropOffRate));
                        return (
                            <div key={stage.key} style={funnelStageStyle}>
                                <div style={funnelStageHeadStyle}>
                                    <div style={funnelStageLabelStyle}>
                                        <span style={funnelStageIndexStyle}>
                                            {String(index + 1).padStart(2, "0")}
                                        </span>
                                        {stage.label}
                                    </div>
                                    <div style={funnelStageCountStyle}>
                                        {formatters.formatNumber(stage.count)}
                                    </div>
                                </div>
                                <div style={funnelBarTrackStyle}>
                                    <div
                                        style={{
                                            ...funnelBarFillStyle,
                                            width: `${passPct}%`,
                                        }}
                                    />
                                </div>
                                <div style={funnelStageFootStyle}>
                                    <span>Pass-through {formatters.formatPercentage(passPct)}</span>
                                    {index > 0 ? (
                                        <span style={dropOffStyle(dropPct)}>
                                            −{formatters.formatPercentage(dropPct)} drop-off
                                        </span>
                                    ) : (
                                        <span style={mutedFootStyle}>entry point</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
}

function LatencyStagesSection({
    performance,
}: {
    performance: OrdersPerformanceResponse | null;
}) {
    type LatencyRowKey = "inflow" | "exchange" | "outflow" | "totalOrderTime";

    const rows: { key: LatencyRowKey; label: string }[] = [
        { key: "inflow", label: "Created → funds received" },
        { key: "exchange", label: "Funds received → exchanged" },
        { key: "outflow", label: "Exchanged → completed" },
        { key: "totalOrderTime", label: "End-to-end" },
    ];

    return (
        <section style={cardStyle}>
            <header style={sectionHeaderStyle}>
                <div>
                    <h2 style={sectionTitleStyle}>Latency by stage</h2>
                    <p style={sectionSubtitleStyle}>
                        Percentiles per operational segment. Diagnostic shows only end-to-end
                        p95 today — full breakdown here.
                    </p>
                </div>
            </header>

            {!performance ? (
                <EmptyHint label="No latency data for the selected period." />
            ) : (
                <div style={tableScrollStyle}>
                    <table style={latencyTableStyle}>
                        <thead>
                            <tr>
                                <th style={latencyHeadCellStyle("left")}>Stage</th>
                                <th style={latencyHeadCellStyle("right")}>Orders</th>
                                <th style={latencyHeadCellStyle("right")}>p50</th>
                                <th style={latencyHeadCellStyle("right")}>p95</th>
                                <th style={latencyHeadCellStyle("right")}>Max</th>
                                <th style={latencyHeadCellStyle("right")}>SLA</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(({ key, label }) => {
                                const metric = performance[key];
                                const slaTone = latencyTone(metric.p95Minutes);
                                return (
                                    <tr key={key}>
                                        <td style={latencyCellStyle("left")}>{label}</td>
                                        <td style={latencyCellStyle("right")}>
                                            {formatters.formatNumber(metric.count)}
                                        </td>
                                        <td style={latencyCellStyle("right")}>
                                            {formatters.formatDuration(metric.p50Minutes)}
                                        </td>
                                        <td style={latencyCellStyle("right")}>
                                            {formatters.formatDuration(metric.p95Minutes)}
                                        </td>
                                        <td style={latencyCellStyle("right")}>
                                            {formatters.formatDuration(metric.maxMinutes)}
                                        </td>
                                        <td style={latencyCellStyle("right")}>
                                            <span style={slaPillStyle(slaTone)}>
                                                {slaTone === "ok"
                                                    ? "OK"
                                                    : slaTone === "watch"
                                                      ? "Watch"
                                                      : "Breach"}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                            <tr>
                                <td style={latencyFooterCellStyle}>Slow orders</td>
                                <td style={latencyCellStyle("right")}>
                                    {formatters.formatNumber(performance.slowOrders.count)}
                                </td>
                                <td style={latencyCellStyle("right")} colSpan={2}>
                                    {formatters.formatPercentage(performance.slowOrders.rate)} of period
                                </td>
                                <td style={latencyCellStyle("right")} colSpan={2}>
                                    threshold {formatters.formatDuration(performance.slowOrders.thresholdMs / 60000)}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

function MonetizationDetailsSection({
    monetization,
}: {
    monetization: OrdersMonetizationResponse | null;
}) {
    return (
        <section style={cardStyle}>
            <header style={sectionHeaderStyle}>
                <div>
                    <h2 style={sectionTitleStyle}>Monetization breakdown</h2>
                    <p style={sectionSubtitleStyle}>
                        Fee per order, network and gas fees — calculated by the backend but
                        not surfaced in the main views.
                    </p>
                </div>
            </header>

            {!monetization ? (
                <EmptyHint label="No monetization data." />
            ) : (
                <div style={statsGridStyle}>
                    <StatCell
                        label="Total business revenue"
                        value={formatters.formatCurrency(monetization.totalBusinessRevenue)}
                    />
                    <StatCell
                        label="Fee per order"
                        value={formatters.formatCurrency(monetization.feePerOrder)}
                    />
                    <StatCell
                        label="Network fee total"
                        value={formatters.formatCurrency(monetization.gasAndNetworkFees.networkFeeTotal)}
                    />
                    <StatCell
                        label="Gas fee total"
                        value={formatters.formatCurrency(monetization.gasAndNetworkFees.gasFeeTotal)}
                    />
                    <StatCell
                        label="Final gas fee"
                        value={formatters.formatCurrency(monetization.gasAndNetworkFees.finalGasFeeTotal)}
                    />
                    <StatCell
                        label="Net revenue (derived)"
                        value={formatters.formatCurrency(
                            monetization.totalBusinessRevenue -
                                monetization.gasAndNetworkFees.finalGasFeeTotal
                        )}
                        helper={`gross − final gas fee`}
                    />
                </div>
            )}
        </section>
    );
}

function CustomersSection({
    customers,
}: {
    customers: OrdersCustomersResponse | null;
}) {
    return (
        <section style={cardStyle}>
            <header style={sectionHeaderStyle}>
                <div>
                    <h2 style={sectionTitleStyle}>Customer metrics</h2>
                    <p style={sectionSubtitleStyle}>
                        Active workspaces, retention, average behaviour per workspace.
                    </p>
                </div>
            </header>

            {!customers ? (
                <EmptyHint label="No customer data." />
            ) : (
                <div style={statsGridStyle}>
                    <StatCell
                        label="Active workspaces"
                        value={formatters.formatNumber(customers.activeUniqueUsers)}
                    />
                    <StatCell
                        label="New workspaces"
                        value={formatters.formatNumber(customers.newUsers)}
                    />
                    <StatCell
                        label="Recurring workspaces"
                        value={formatters.formatNumber(customers.recurringUsers)}
                    />
                    <StatCell
                        label="Repeat rate"
                        value={formatters.formatPercentage(customers.repeatRate)}
                    />
                    <StatCell
                        label="Orders per workspace"
                        value={customers.ordersPerUser.toFixed(2)}
                    />
                    <StatCell
                        label="Volume per workspace"
                        value={formatters.formatCurrency(customers.volumePerUser)}
                    />
                    <StatCell
                        label="Revenue per workspace"
                        value={formatters.formatCurrency(customers.businessRevenuePerUser)}
                    />
                </div>
            )}
        </section>
    );
}

function StatCell({
    label,
    value,
    helper,
}: {
    label: string;
    value: string;
    helper?: string;
}) {
    return (
        <div style={statCellStyle}>
            <span style={statLabelStyle}>{label}</span>
            <strong style={statValueStyle}>{value}</strong>
            {helper ? <small style={statHelperStyle}>{helper}</small> : null}
        </div>
    );
}

function EmptyHint({ label }: { label: string }) {
    return <div style={emptyHintStyle}>{label}</div>;
}

type LatencyTone = "ok" | "watch" | "breach";

function latencyTone(p95Minutes: number): LatencyTone {
    if (p95Minutes <= 30) return "ok";
    if (p95Minutes <= 60) return "watch";
    return "breach";
}

function dropOffStyle(dropPct: number): CSSProperties {
    const color =
        dropPct >= 30
            ? analyticsTheme.colors.danger
            : dropPct >= 10
              ? analyticsTheme.colors.warning
              : analyticsTheme.colors.textMuted;
    return { color, fontWeight: 700 };
}

function slaPillStyle(tone: LatencyTone): CSSProperties {
    const palette =
        tone === "ok"
            ? { bg: analyticsTheme.colors.successBg, color: analyticsTheme.colors.success }
            : tone === "watch"
              ? { bg: analyticsTheme.colors.warningBg, color: analyticsTheme.colors.warning }
              : { bg: analyticsTheme.colors.dangerBg, color: analyticsTheme.colors.danger };
    return {
        background: palette.bg,
        color: palette.color,
        padding: "2px 10px",
        borderRadius: "999px",
        fontWeight: 700,
        fontSize: "11px",
        display: "inline-block",
    };
}

const shellStyle: CSSProperties = {
    width: "100%",
    minWidth: 0,
    minHeight: 0,
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: "4px",
};

const twoColumnGridStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
    gap: "16px",
    minWidth: 0,
};

const inlineCodeStyle: CSSProperties = {
    fontFamily: "Menlo, monospace",
    fontSize: "0.72rem",
    background: analyticsTheme.colors.pageBgSoft,
    padding: "1px 6px",
    borderRadius: "4px",
    border: `1px solid ${analyticsTheme.colors.border}`,
};

const kpiStripStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(180px, 100%), 1fr))",
    gap: "10px",
};

const kpiCellStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: "12px 14px",
    background: analyticsTheme.colors.pageBgSoft,
    border: `1px solid ${analyticsTheme.colors.border}`,
    borderRadius: "12px",
    minWidth: 0,
};

const kpiCellLabelStyle: CSSProperties = {
    color: analyticsTheme.colors.textMuted,
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
};

const kpiCellValueStyle: CSSProperties = {
    color: analyticsTheme.colors.textPrimary,
    fontSize: "1.05rem",
    fontWeight: 800,
    fontVariantNumeric: "tabular-nums",
    overflowWrap: "anywhere",
    lineHeight: 1.15,
};

const kpiCellFootStyle: CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
};

const kpiCellDeltaStyle = (tone: KpiCellTone): CSSProperties => {
    const palette =
        tone === "ok"
            ? analyticsTheme.colors.success
            : tone === "warning"
              ? analyticsTheme.colors.warning
              : tone === "danger"
                ? analyticsTheme.colors.danger
                : analyticsTheme.colors.textMuted;
    return {
        color: palette,
        fontSize: "0.74rem",
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
    };
};

const kpiCellHelperStyle: CSSProperties = {
    color: analyticsTheme.colors.textMuted,
    fontSize: "0.7rem",
    fontWeight: 500,
};

const riskMainRowStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 140px) minmax(0, 1fr)",
    gap: "20px",
    alignItems: "stretch",
};

const riskNumberWrapStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "12px",
    background: analyticsTheme.colors.pageBgSoft,
    border: `1px solid ${analyticsTheme.colors.border}`,
    borderRadius: "16px",
};

const riskNumberStyle: CSSProperties = {
    fontSize: "3rem",
    fontWeight: 900,
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.02em",
};

const riskSeverityPillStyle: CSSProperties = {
    padding: "3px 12px",
    borderRadius: "999px",
    border: "1px solid",
    fontSize: "10px",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
};

const riskContributionsStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    minWidth: 0,
};

const riskRowStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
};

const riskRowHeadStyle: CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: "12px",
    minWidth: 0,
};

const riskRowLabelStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "baseline",
    gap: "8px",
    color: analyticsTheme.colors.textPrimary,
    fontSize: "0.78rem",
    fontWeight: 700,
};

const riskWeightStyle: CSSProperties = {
    color: analyticsTheme.colors.textMuted,
    fontSize: "0.66rem",
    fontWeight: 600,
};

const riskRowValueStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "baseline",
    gap: "8px",
    color: analyticsTheme.colors.textPrimary,
    fontSize: "0.78rem",
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
};

const riskRowNoteStyle: CSSProperties = {
    color: analyticsTheme.colors.textMuted,
    fontSize: "0.66rem",
    fontWeight: 500,
};

const riskBarTrackStyle: CSSProperties = {
    height: "6px",
    width: "100%",
    background: analyticsTheme.colors.pageBgSoft,
    border: `1px solid ${analyticsTheme.colors.border}`,
    borderRadius: "999px",
    overflow: "hidden",
};

const riskBarFillStyle: CSSProperties = {
    height: "100%",
    transition: "width 220ms ease",
};

const dimensionPillStyle: CSSProperties = {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "999px",
    background: analyticsTheme.colors.primarySoft,
    color: analyticsTheme.colors.primary,
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "lowercase",
};

const rankBadgeStyle: CSSProperties = {
    display: "inline-block",
    minWidth: "22px",
    padding: "2px 6px",
    textAlign: "center",
    background: analyticsTheme.colors.primary,
    color: "#FFFFFF",
    borderRadius: "6px",
    fontSize: "0.7rem",
    fontWeight: 800,
    fontFamily: "Menlo, monospace",
};

const primaryIssueStyle = (tone: KpiCellTone): CSSProperties => {
    const palette =
        tone === "ok"
            ? { bg: analyticsTheme.colors.successBg, color: analyticsTheme.colors.success }
            : tone === "warning"
              ? { bg: analyticsTheme.colors.warningBg, color: analyticsTheme.colors.warning }
              : tone === "danger"
                ? { bg: analyticsTheme.colors.dangerBg, color: analyticsTheme.colors.danger }
                : { bg: analyticsTheme.colors.pageBgSoft, color: analyticsTheme.colors.textSecondary };
    return {
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "999px",
        background: palette.bg,
        color: palette.color,
        fontSize: "0.72rem",
        fontWeight: 700,
        marginRight: "6px",
    };
};

const primaryNoteStyle: CSSProperties = {
    color: analyticsTheme.colors.textMuted,
    fontSize: "0.7rem",
    fontWeight: 500,
};

const anomalyCountersStyle: CSSProperties = {
    display: "flex",
    gap: "12px",
    flex: "0 0 auto",
};

const anomalyCounterStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "6px 12px",
    borderRadius: "8px",
    background: analyticsTheme.colors.pageBgSoft,
    border: `1px solid ${analyticsTheme.colors.border}`,
    color: analyticsTheme.colors.textPrimary,
    fontSize: "0.92rem",
    fontWeight: 800,
    fontVariantNumeric: "tabular-nums",
    minWidth: "62px",
};

const anomalyListStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minWidth: 0,
};

const anomalyRowStyle = (tone: KpiCellTone): CSSProperties => {
    const accent =
        tone === "danger"
            ? analyticsTheme.colors.danger
            : tone === "warning"
              ? analyticsTheme.colors.warning
              : analyticsTheme.colors.borderStrong;
    return {
        borderLeft: `3px solid ${accent}`,
        background: analyticsTheme.colors.pageBgSoft,
        padding: "10px 12px",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        minWidth: 0,
    };
};

const anomalyRowHeadStyle: CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: "10px",
    minWidth: 0,
};

const anomalyBadgeStyle = (tone: KpiCellTone): CSSProperties => {
    const palette =
        tone === "danger"
            ? { bg: analyticsTheme.colors.dangerBg, color: analyticsTheme.colors.danger }
            : tone === "warning"
              ? { bg: analyticsTheme.colors.warningBg, color: analyticsTheme.colors.warning }
              : { bg: analyticsTheme.colors.pageBgSoft, color: analyticsTheme.colors.textSecondary };
    return {
        background: palette.bg,
        color: palette.color,
        padding: "2px 10px",
        borderRadius: "999px",
        fontSize: "0.74rem",
        fontWeight: 800,
        letterSpacing: "0.02em",
    };
};

const anomalyDateStyle: CSSProperties = {
    color: analyticsTheme.colors.textMuted,
    fontSize: "0.74rem",
    fontWeight: 600,
};

const anomalyRowBodyStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(110px, 100%), 1fr))",
    gap: "6px 14px",
    color: analyticsTheme.colors.textPrimary,
    fontSize: "0.82rem",
    fontVariantNumeric: "tabular-nums",
};

const anomalyLabelStyle: CSSProperties = {
    display: "block",
    color: analyticsTheme.colors.textMuted,
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    marginBottom: "2px",
};

const concentrationGridStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 140px) minmax(0, 1fr)",
    gap: "16px",
    alignItems: "stretch",
};

const hhiBlockStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "12px",
    background: analyticsTheme.colors.pageBgSoft,
    border: `1px solid ${analyticsTheme.colors.border}`,
    borderRadius: "16px",
};

const hhiNumberStyle: CSSProperties = {
    fontSize: "1.9rem",
    fontWeight: 900,
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.02em",
};

const hhiBadgeStyle: CSSProperties = {
    padding: "3px 10px",
    borderRadius: "999px",
    border: "1px solid",
    fontSize: "10px",
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    textAlign: "center",
};

const shareRowsStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    minWidth: 0,
};

const shareRowStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
};

const shareRowHeadStyle: CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
};

const shareRowLabelStyle: CSSProperties = {
    color: analyticsTheme.colors.textSecondary,
    fontSize: "0.78rem",
    fontWeight: 700,
};

const shareRowValueStyle: CSSProperties = {
    color: analyticsTheme.colors.textPrimary,
    fontSize: "0.82rem",
    fontWeight: 800,
    fontVariantNumeric: "tabular-nums",
};

const topThreeListStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    paddingTop: "8px",
    borderTop: `1px dashed ${analyticsTheme.colors.border}`,
};

const topThreeRowStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "auto minmax(0, 1fr) auto",
    gap: "10px",
    alignItems: "baseline",
};

const topThreeRankStyle: CSSProperties = {
    fontFamily: "Menlo, monospace",
    fontSize: "0.7rem",
    fontWeight: 800,
    color: analyticsTheme.colors.textMuted,
    minWidth: "24px",
};

const topThreeNameStyle: CSSProperties = {
    color: analyticsTheme.colors.textPrimary,
    fontSize: "0.84rem",
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};

const topThreeShareStyle: CSSProperties = {
    color: analyticsTheme.colors.textPrimary,
    fontSize: "0.82rem",
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
};

const topThreePctStyle: CSSProperties = {
    color: analyticsTheme.colors.textMuted,
    fontSize: "0.7rem",
    fontWeight: 500,
    marginLeft: "4px",
};

const hourlyChartStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(24, minmax(0, 1fr))",
    gap: "4px",
    minHeight: "140px",
    alignItems: "end",
};

const hourlyBarColumnStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
    minWidth: 0,
};

const hourlyBarTrackStyle: CSSProperties = {
    width: "100%",
    height: "120px",
    background: analyticsTheme.colors.pageBgSoft,
    border: `1px solid ${analyticsTheme.colors.border}`,
    borderRadius: "4px",
    display: "flex",
    alignItems: "flex-end",
    overflow: "hidden",
};

const hourlyBarFillStyle: CSSProperties = {
    width: "100%",
    minHeight: "1px",
    transition: "height 220ms ease",
};

const hourlyHourLabelStyle: CSSProperties = {
    fontSize: "0.6rem",
    fontWeight: 700,
    color: analyticsTheme.colors.textMuted,
    fontFamily: "Menlo, monospace",
};

const heatmapLegendStyle: CSSProperties = {
    display: "flex",
    gap: "6px",
    flex: "0 0 auto",
};

const heatmapLegendItemStyle = (tone: LatencyTone): CSSProperties => {
    const palette =
        tone === "ok"
            ? { bg: analyticsTheme.colors.successBg, color: analyticsTheme.colors.success }
            : tone === "watch"
              ? { bg: analyticsTheme.colors.warningBg, color: analyticsTheme.colors.warning }
              : { bg: analyticsTheme.colors.dangerBg, color: analyticsTheme.colors.danger };
    return {
        background: palette.bg,
        color: palette.color,
        padding: "2px 8px",
        borderRadius: "6px",
        fontSize: "0.68rem",
        fontWeight: 800,
        fontFamily: "Menlo, monospace",
    };
};

const heatmapWrapStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    minWidth: 0,
};

const heatmapHeaderStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "minmax(150px, 1.6fr) repeat(3, minmax(0, 1fr))",
    gap: "8px",
    alignItems: "center",
};

const heatmapColHeaderStyle: CSSProperties = {
    textAlign: "center",
    color: analyticsTheme.colors.textMuted,
    fontSize: "0.7rem",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
};

const heatmapRowStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "minmax(150px, 1.6fr) repeat(3, minmax(0, 1fr))",
    gap: "8px",
    alignItems: "stretch",
};

const heatmapRowLabelStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    color: analyticsTheme.colors.textPrimary,
    fontSize: "0.82rem",
    fontWeight: 700,
};

const heatmapCellStyle = (tone: LatencyTone): CSSProperties => {
    const palette =
        tone === "ok"
            ? {
                  bg: analyticsTheme.colors.successBg,
                  border: analyticsTheme.colors.success,
                  color: analyticsTheme.colors.success,
              }
            : tone === "watch"
              ? {
                    bg: analyticsTheme.colors.warningBg,
                    border: analyticsTheme.colors.warning,
                    color: analyticsTheme.colors.warning,
                }
              : {
                    bg: analyticsTheme.colors.dangerBg,
                    border: analyticsTheme.colors.danger,
                    color: analyticsTheme.colors.danger,
                };
    return {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "10px 12px",
        background: palette.bg,
        border: `1px solid ${palette.border}40`,
        borderRadius: "8px",
        color: palette.color,
        fontVariantNumeric: "tabular-nums",
        fontWeight: 800,
        fontSize: "0.92rem",
    };
};

const distributionListStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
};

const distributionRowStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "minmax(140px, 0.8fr) minmax(0, 2fr)",
    gap: "16px",
    alignItems: "center",
};

const distributionLabelStyle: CSSProperties = {
    color: analyticsTheme.colors.textPrimary,
    fontSize: "0.86rem",
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};

const distributionBarsStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minWidth: 0,
};

const distributionBarItemStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
};

const distributionBarHeadStyle: CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: "12px",
    minWidth: 0,
};

const distributionBarLegendStyle = (color: string): CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    color: analyticsTheme.colors.textSecondary,
    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    paddingLeft: "14px",
    backgroundImage: `radial-gradient(circle at 4px center, ${color} 3px, transparent 4px)`,
    backgroundRepeat: "no-repeat",
});

const distributionBarValueStyle: CSSProperties = {
    color: analyticsTheme.colors.textPrimary,
    fontSize: "0.84rem",
    fontWeight: 800,
    fontVariantNumeric: "tabular-nums",
};

const dimensionSwitcherStyle: CSSProperties = {
    background: analyticsTheme.colors.cardBg,
    border: `1px solid ${analyticsTheme.colors.border}`,
    borderRadius: "12px",
    padding: "12px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    minWidth: 0,
};

const dimensionSwitcherLabelStyle: CSSProperties = {
    display: "flex",
    alignItems: "baseline",
    gap: "10px",
    flexWrap: "wrap",
    minWidth: 0,
};

const dimensionSwitcherTitleStyle: CSSProperties = {
    color: analyticsTheme.colors.textPrimary,
    fontSize: "0.8rem",
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
};

const dimensionSwitcherHintStyle: CSSProperties = {
    color: analyticsTheme.colors.textMuted,
    fontSize: "0.72rem",
    fontWeight: 500,
};

const dimensionChipGroupStyle: CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
};

const dimensionChipStyle = (active: boolean): CSSProperties => ({
    padding: "5px 12px",
    border: `1px solid ${active ? analyticsTheme.colors.primary : analyticsTheme.colors.borderStrong}`,
    background: active ? analyticsTheme.colors.primary : analyticsTheme.colors.cardBg,
    color: active ? "#FFFFFF" : analyticsTheme.colors.textSecondary,
    borderRadius: "999px",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.02em",
    cursor: "pointer",
    fontFamily: "inherit",
});

const bestSegmentsListStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
};

const bestSegmentRowStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "auto minmax(0, 1fr)",
    gap: "12px",
    alignItems: "center",
    padding: "10px 12px",
    background: analyticsTheme.colors.pageBgSoft,
    border: `1px solid ${analyticsTheme.colors.border}`,
    borderRadius: "10px",
};

const bestSegmentBodyStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    minWidth: 0,
};

const bestSegmentHeadStyle: CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: "10px",
    minWidth: 0,
};

const bestSegmentNameStyle: CSSProperties = {
    color: analyticsTheme.colors.textPrimary,
    fontSize: "0.92rem",
    fontWeight: 800,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};

const bestSegmentScoreStyle: CSSProperties = {
    fontSize: "1.2rem",
    fontWeight: 900,
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
};

const bestSegmentMetaStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(96px, 100%), 1fr))",
    gap: "4px 12px",
    color: analyticsTheme.colors.textPrimary,
    fontSize: "0.8rem",
    fontVariantNumeric: "tabular-nums",
};

const scatterWrapStyle: CSSProperties = {
    width: "100%",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
};

const scatterLegendStyle: CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px 16px",
    color: analyticsTheme.colors.textSecondary,
    fontSize: "0.72rem",
    fontWeight: 700,
};

const legendDotWrapStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
};

const legendDotMarkerStyle: CSSProperties = {
    width: "8px",
    height: "8px",
    borderRadius: "999px",
    border: "1px solid rgba(255,255,255,0.9)",
    boxShadow: "0 0 0 1px rgba(15, 35, 65, 0.1)",
};

const bottleneckHeroStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 160px) minmax(0, 1fr)",
    gap: "16px",
    alignItems: "stretch",
};

const bottleneckHeroIconStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "16px 12px",
    border: "1px solid",
    borderRadius: "14px",
    textAlign: "center",
};

const bottleneckHeroLabelStyle: CSSProperties = {
    fontSize: "0.66rem",
    fontWeight: 800,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
};

const bottleneckHeroNameStyle: CSSProperties = {
    color: analyticsTheme.colors.textPrimary,
    fontSize: "1.1rem",
    fontWeight: 800,
    lineHeight: 1.15,
};

const bottleneckHeroMetaStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(110px, 100%), 1fr))",
    gap: "8px 14px",
    color: analyticsTheme.colors.textPrimary,
    fontSize: "0.86rem",
    fontVariantNumeric: "tabular-nums",
    alignContent: "center",
};

const bottleneckListStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    paddingTop: "10px",
    borderTop: `1px dashed ${analyticsTheme.colors.border}`,
};

const bottleneckRowStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
};

const bottleneckRowHeadStyle: CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: "10px",
    minWidth: 0,
};

const bottleneckRowLabelStyle: CSSProperties = {
    color: analyticsTheme.colors.textPrimary,
    fontSize: "0.84rem",
    fontWeight: 700,
};

const bottleneckRowValueStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "baseline",
    gap: "8px",
    color: analyticsTheme.colors.textPrimary,
    fontSize: "0.84rem",
    fontWeight: 800,
    fontVariantNumeric: "tabular-nums",
};

const bottleneckRowNoteStyle: CSSProperties = {
    color: analyticsTheme.colors.textMuted,
    fontSize: "0.68rem",
    fontWeight: 500,
};

const experimentalBannerStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 14px",
    background: "linear-gradient(135deg, rgba(106, 77, 204, 0.10) 0%, rgba(50, 88, 216, 0.06) 100%)",
    border: "1px solid rgba(106, 77, 204, 0.28)",
    borderRadius: "12px",
    color: analyticsTheme.colors.textSecondary,
    fontSize: "12px",
    lineHeight: 1.45,
};

const bannerLabelStyle: CSSProperties = {
    color: analyticsTheme.colors.accentPurple,
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    padding: "2px 8px",
    background: "#FFFFFF",
    borderRadius: "999px",
    border: "1px solid rgba(106, 77, 204, 0.32)",
    flex: "0 0 auto",
};

const bannerCopyStyle: CSSProperties = {
    minWidth: 0,
};

const cardStyle: CSSProperties = {
    background: analyticsTheme.colors.cardBg,
    border: `1px solid ${analyticsTheme.colors.border}`,
    borderRadius: "16px",
    boxShadow: analyticsTheme.shadows.soft,
    padding: "20px 24px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minWidth: 0,
};

const sectionHeaderStyle: CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
};

const sectionTitleStyle: CSSProperties = {
    margin: 0,
    color: analyticsTheme.colors.textPrimary,
    fontSize: "1.05rem",
    fontWeight: 700,
    letterSpacing: "-0.01em",
};

const sectionSubtitleStyle: CSSProperties = {
    margin: "4px 0 0 0",
    color: analyticsTheme.colors.textMuted,
    fontSize: "0.78rem",
    fontWeight: 500,
    lineHeight: 1.45,
    maxWidth: "72ch",
};

const stuckPillStyle: CSSProperties = {
    padding: "6px 12px",
    borderRadius: "999px",
    background: analyticsTheme.colors.warningBg,
    color: analyticsTheme.colors.warning,
    fontSize: "12px",
    fontWeight: 700,
    flex: "0 0 auto",
};

const funnelListStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
};

const funnelStageStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    minWidth: 0,
};

const funnelStageHeadStyle: CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: "12px",
    minWidth: 0,
};

const funnelStageLabelStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    color: analyticsTheme.colors.textPrimary,
    fontSize: "0.9rem",
    fontWeight: 700,
    minWidth: 0,
};

const funnelStageIndexStyle: CSSProperties = {
    fontFamily: "Menlo, monospace",
    fontSize: "10px",
    fontWeight: 700,
    color: analyticsTheme.colors.textMuted,
    background: analyticsTheme.colors.pageBgSoft,
    border: `1px solid ${analyticsTheme.colors.border}`,
    padding: "2px 6px",
    borderRadius: "6px",
    flex: "0 0 auto",
};

const funnelStageCountStyle: CSSProperties = {
    color: analyticsTheme.colors.textPrimary,
    fontSize: "0.92rem",
    fontWeight: 800,
    fontVariantNumeric: "tabular-nums",
};

const funnelBarTrackStyle: CSSProperties = {
    width: "100%",
    height: "10px",
    background: analyticsTheme.colors.pageBgSoft,
    border: `1px solid ${analyticsTheme.colors.border}`,
    borderRadius: "999px",
    overflow: "hidden",
};

const funnelBarFillStyle: CSSProperties = {
    height: "100%",
    background: `linear-gradient(90deg, ${analyticsTheme.colors.accentBlue} 0%, ${analyticsTheme.colors.accentPurple} 100%)`,
    transition: "width 220ms ease",
};

const funnelStageFootStyle: CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    color: analyticsTheme.colors.textMuted,
    fontSize: "0.74rem",
    fontWeight: 600,
};

const mutedFootStyle: CSSProperties = {
    color: analyticsTheme.colors.textMuted,
    fontStyle: "italic",
};

const tableScrollStyle: CSSProperties = {
    width: "100%",
    overflowX: "auto",
    minWidth: 0,
};

const latencyTableStyle: CSSProperties = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.82rem",
    color: analyticsTheme.colors.textPrimary,
};

const latencyHeadCellStyle = (align: "left" | "right"): CSSProperties => ({
    padding: "8px 12px",
    textAlign: align,
    color: analyticsTheme.colors.textMuted,
    fontWeight: 700,
    fontSize: "0.72rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    borderBottom: `1px solid ${analyticsTheme.colors.border}`,
    whiteSpace: "nowrap",
});

const latencyCellStyle = (align: "left" | "right"): CSSProperties => ({
    padding: "10px 12px",
    textAlign: align,
    fontVariantNumeric: "tabular-nums",
    borderBottom: `1px solid ${analyticsTheme.colors.pageBg}`,
    whiteSpace: "nowrap",
});

const latencyFooterCellStyle: CSSProperties = {
    padding: "10px 12px",
    textAlign: "left",
    color: analyticsTheme.colors.textMuted,
    fontWeight: 700,
    fontSize: "0.78rem",
    borderTop: `1px solid ${analyticsTheme.colors.border}`,
    borderBottom: `1px solid ${analyticsTheme.colors.pageBg}`,
};

const statsGridStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(180px, 100%), 1fr))",
    gap: "12px",
};

const statCellStyle: CSSProperties = {
    background: analyticsTheme.colors.pageBgSoft,
    border: `1px solid ${analyticsTheme.colors.border}`,
    borderRadius: "12px",
    padding: "12px 14px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
};

const statLabelStyle: CSSProperties = {
    color: analyticsTheme.colors.textMuted,
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
};

const statValueStyle: CSSProperties = {
    color: analyticsTheme.colors.textPrimary,
    fontSize: "1.1rem",
    fontWeight: 800,
    fontVariantNumeric: "tabular-nums",
    overflowWrap: "anywhere",
};

const statHelperStyle: CSSProperties = {
    color: analyticsTheme.colors.textMuted,
    fontSize: "0.7rem",
    fontWeight: 500,
};

const emptyHintStyle: CSSProperties = {
    padding: "24px",
    textAlign: "center",
    color: analyticsTheme.colors.textMuted,
    fontSize: "0.82rem",
    fontWeight: 500,
    background: analyticsTheme.colors.pageBgSoft,
    border: `1px dashed ${analyticsTheme.colors.border}`,
    borderRadius: "12px",
};
